/**
 * Pure decision logic for the member page's per-note edit + delete controls
 * (2026-08-06).
 *
 * The Session-notes card dispatches `edit_session_note` / `delete_session_note`
 * — the SAME canonical actions the CLI and MCP call, so there is exactly one
 * write path onto `session_notes` (invariant 5). This module is the part that
 * decides, extracted so it can be tested without a DOM: the web tree carries no
 * jsdom, so component logic worth guarding lives in `lib/` and is unit-tested
 * there (the `pr-person-edit.ts` pattern).
 *
 * Both rules below exist to stop a specific wrong outcome, not for tidiness:
 *
 *   - a no-op save would file an `edit_session_note` audit row recording a
 *     change that did not happen, and the audit row is the ONLY record of a
 *     note's history;
 *   - a delete is a HARD delete with no undo, and a note carrying a safety flag
 *     also removes that flag from the banner every trainer reads before a
 *     session — a consequence the operator cannot see from the delete button.
 */

/**
 * Mirrors the `body` bound in `edit_session_note`'s input schema
 * (`minLength: 1`, `maxLength: 4000`). The chokepoint remains the sole
 * enforcement layer; this is here so the textarea stops the operator at the
 * limit rather than letting them collect an `input_invalid`.
 */
export const SESSION_NOTE_MAX_LENGTH = 4000;

/**
 * May this draft be saved?
 *
 * `false` for an empty/whitespace-only draft (the action's `minLength: 1` would
 * reject it) and for one that is unchanged after trimming (dispatching it would
 * write an audit row asserting an edit that changed nothing).
 */
export function canSaveSessionNote(draft: string, original: string): boolean {
  const trimmed = draft.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > SESSION_NOTE_MAX_LENGTH) return false;
  return trimmed !== original.trim();
}

/** The exact value dispatched as `body` — trimmed, never the raw draft. */
export function sessionNoteSaveBody(draft: string): string {
  return draft.trim();
}

/**
 * The confirm text shown before a hard delete. A flagged note gets the sharper
 * wording because deleting it silently removes a safety flag from the member's
 * banner, which is a second consequence the button itself does not express.
 */
export function sessionNoteDeleteConfirmMessage(hasFlags: boolean): string {
  return hasFlags
    ? "Delete this note? It carries a safety flag, which will disappear from the member's Safety flags banner. This cannot be undone."
    : "Delete this note permanently? This cannot be undone.";
}
